/**
 * Die Seiten des Bereichs „Grundlagen" und der Weg dorthin.
 *
 * ## Warum ein Hash und kein Pfad
 *
 * Die vier offenen Wege dieser Anwendung sind Pfade (`/geteilt/<token>`,
 * `/einladung/…`, `/mitplanen/…`, `/hilfe`), und jeder davon ist eine eigene
 * **Seite**: Wer sie öffnet, bleibt dort. Die Grundlagen sind das nicht — sie
 * sind eine zweite Fläche derselben angemeldeten Anwendung, und man kommt aus
 * ihnen zurück in die Reise, in der man gerade saß.
 *
 * Ein Hash kostet dafür nichts: Er verlangt keine Server-Regel (nginx müsste
 * einen neuen Pfad auf `index.html` legen), er reißt das Fenster nicht neu
 * auf, und die Reise, die in `App.svelte` offen liegt, bleibt offen. Genau
 * deshalb steht er im Vertrag (§ 4.1) und nicht `/grundlagen`.
 *
 * ## Warum die Regeln hier stehen und nicht in `Grundlagen.svelte`
 *
 * Aus demselben Grund, aus dem `wege.ts` existiert: Ein zu großzügiges Muster
 * fängt Hashes, die es nicht fangen soll (`#grundlagenkram`), ein zu enges
 * lässt genau die durchfallen, die die Anwendung selbst schreibt. Beides sieht
 * man dem Ausdruck nicht an — man sieht es an Beispielen, und die stehen im
 * Test.
 *
 * Eine **unbekannte** Seite führt hier bewusst auf die erste und nicht ins
 * Leere: `#/grundlagen/ladekurve` ist ein Tippfehler oder ein Link aus einer
 * älteren Fassung, und die ehrlichste Antwort darauf ist die Fläche, die es
 * gibt. Ein leerer Bereich sähe aus wie ein Fehler der Anwendung.
 */

/** Die Kennung einer Seite — zugleich das Stück hinter dem Schrägstrich. */
export type Grundlagenseite =
  | 'fahrzeug'
  | 'ladekarten'
  | 'packliste'
  | 'reiseprofile'
  | 'konto';

/** Eine Seite, wie Seitenleiste, Reiter und Befehlssuche sie brauchen. */
export interface Grundlagenblatt {
  id: Grundlagenseite;
  titel: string;
  /** Was auf der Seite steht, in einem Halbsatz — Seitenleiste und Cmd-K. */
  zeile: string;
  /** Wörter, unter denen man sie sucht, ohne dass sie im Titel stehen. */
  schlagworte: readonly string[];
  /**
   * Kommt die Seite erst in Runde 2?
   *
   * Steht hier und nicht in der Komponente, weil das Routing es wissen muss:
   * Die Seite ist **da** (sonst wäre der Reiter ein toter Link), sie sagt nur
   * noch nichts. Ein weggelassener Reiter hätte bedeutet, dass die Reihenfolge
   * der Seiten sich in Runde 2 ändert — und Lesezeichen auf `#/grundlagen`
   * zeigten dann woandershin.
   */
  spaeter?: boolean;
}

/**
 * Die fünf Seiten in der Reihenfolge des Vertrags (§ 4.1).
 *
 * Sie ist keine Geschmacksfrage: Fahrzeug zuerst, weil daran das meiste hängt
 * (Laden, Maut, Packen); Konto zuletzt, weil dort das Abmelden steht und die
 * schwerere Handlung unter der leichteren gehört (dieselbe Überlegung wie in
 * den alten Profileinstellungen).
 */
export const SEITEN: readonly Grundlagenblatt[] = [
  {
    id: 'fahrzeug',
    titel: 'Fahrzeug',
    zeile: 'Stammdaten, Kennzeichen, Maße, Vignetten, ZTL, Camping, Winter',
    schlagworte: ['auto', 'wagen', 'akku', 'verbrauch', 'stecker', 'ladekurve', 'kennzeichen', 'ztl', 'vignette', 'masse', 'hoehe', 'winterreifen', 'v2l']
  },
  {
    id: 'ladekarten',
    titel: 'Ladekarten',
    zeile: 'deine Karten in Rangfolge, Preise je Land und Art, Vorlagen',
    schlagworte: ['tarif', 'tarife', 'ionity', 'enbw', 'adac', 'roaming', 'preis', 'kwh', 'abo'],
    // Seit dem 12.9.2026 (Runde 2) steht die Seite. Der Schalter bleibt
    // ausdrücklich stehen und wird nicht gelöscht: Er beschreibt einen
    // Zustand, den es wieder geben wird, sobald eine sechste Seite angekündigt
    // ist — und die Marke „bald" in der Seitenleiste hängt daran.
    spaeter: false
  },
  {
    id: 'packliste',
    titel: 'Packliste',
    zeile: 'die Liste, mit der jede neue Reise anfängt — samt Bedingungen',
    schlagworte: ['packen', 'standard', 'vorlage', 'merkmal', 'merkmale', 'bedingung']
  },
  {
    id: 'reiseprofile',
    titel: 'Reiseprofile',
    zeile: 'Vorgaben für neue Reisen: Rhythmus, Quartier, Laden, Lotte',
    schlagworte: ['profil', 'profile', 'vorgabe', 'vorlage', 'rhythmus', 'quartier'],
    spaeter: false
  },
  {
    id: 'konto',
    titel: 'Konto und Zugang',
    zeile: 'Name, Expertenansicht, Zuhause, Vertrauenspersonen, Freigaben, Speicher',
    schlagworte: ['profil', 'abmelden', 'logout', 'zuhause', 'adresse', 'lotte', 'freigabe', 'speicher', 'medien', 'experte']
  }
];

/** Die erste Seite — die Antwort auf `#/grundlagen` und auf einen Tippfehler. */
export const ERSTE_SEITE: Grundlagenseite = SEITEN[0].id;

/** Der Rumpf des Hashwegs, an einer Stelle — Link und Erkennung dürfen nicht auseinanderlaufen. */
export const GRUNDLAGENHASH = '#/grundlagen';

/*
 * `SEITENLEISTE_AB` stand hier bis zum 14.9.2026 — 900 px, ab denen die
 * Seitenwahl dieses Bereichs links stand.
 *
 * Die Seitenwahl ist mit dem Navigationsumbau in die Seitenleiste der
 * Anwendung gewandert (Vertrag NAVIGATION A, § 1), und die hat ihre eigene
 * Grenze: `navigation/phasen.LEISTE_AB`. Zwei Zahlen für dieselbe Frage
 * wären zwei Fenster, in denen die Navigation an verschiedenen Stellen
 * umspringt.
 */

/** `#/grundlagen/<seite>` — der Weg zu einer Seite. */
export function hashFuer(seite: Grundlagenseite): string {
  return `${GRUNDLAGENHASH}/${seite}`;
}

/**
 * Zeigt dieser Hash in die Grundlagen?
 *
 * `#/grundlagen`, `#/grundlagen/`, `#/grundlagen/konto` — ja.
 * `#grundlagen`, `#/grundlagenkram`, `#kosten` — nein. Der führende
 * Schrägstrich ist Absicht: Er unterscheidet einen **Weg** von einer
 * Sprungmarke, und Sprungmarken gibt es in dieser Anwendung schon (die Hilfe
 * arbeitet mit `#kosten`).
 */
export function istGrundlagenhash(hash: string): boolean {
  return /^#\/grundlagen(\/[A-Za-z0-9_-]*)?\/?$/.test(hash);
}

/**
 * Die gemeinte Seite, oder `null`, wenn der Hash nicht hierher zeigt.
 *
 * `null` heißt „nicht mein Weg" und ist damit die Weiche in `App.svelte`; eine
 * unbekannte **Seite** dagegen ergibt die erste (siehe Modulkopf). Die zwei
 * Fälle auseinanderzuhalten ist der ganze Zweck dieser Funktion: Ein
 * Rückfall, der auch bei `#kosten` die Grundlagen aufschlägt, riss aus der
 * Hilfe heraus.
 */
export function seiteAusHash(hash: string): Grundlagenseite | null {
  if (!istGrundlagenhash(hash)) return null;
  const rest = hash.slice(GRUNDLAGENHASH.length).replace(/^\/|\/$/g, '');
  const treffer = SEITEN.find((s) => s.id === rest);
  return treffer ? treffer.id : ERSTE_SEITE;
}

/** Das Blatt zu einer Kennung. Wirft bei einer unbekannten — siehe `gliederung.titel`. */
export function blatt(seite: Grundlagenseite): Grundlagenblatt {
  const treffer = SEITEN.find((s) => s.id === seite);
  if (!treffer) throw new Error(`Keine Grundlagen-Seite „${seite}“.`);
  return treffer;
}
